import React, { useCallback, useMemo, useRef, useState } from "react";

import Address from "@mapmycustomers/shared/types/Address";
import getFormattedAddressForUi from "@mapmycustomers/shared/util/formatters/getFormattedAddressForUi";

import { AddressPickDetails, AddressPreset, AddressPresets } from "./AddressPresets";
import useGoogleAutoCompleteOptions from "./useGoogleAutoCompleteOptions";

const PRESET_VALUE_PREFIX = "preset:";
const SHOW_ON_FOCUS: ("click" | "focus")[] = ["focus"];

const getPresetValue = ({ key }: AddressPreset) => `${PRESET_VALUE_PREFIX}${key}`;

const useAddressPresets = ({
  fieldValue,
  onBlur,
  onChange,
  onEditEnd,
  onKeyDown,
  onSelect,
  onTextBlur,
  options,
  presets,
  setFieldValue,
  setFieldValueWithoutSearch,
  setPlacesList,
  value,
}: {
  fieldValue: string;
  onBlur?: () => void;
  onChange?: (address?: Address, details?: AddressPickDetails) => void;
  onEditEnd?: () => void;
  onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => void;
  onSelect: (value: string) => void;
  onTextBlur: () => void;
  options: ReturnType<typeof useGoogleAutoCompleteOptions>;
  presets?: AddressPresets;
  setFieldValue: (text: string) => void;
  setFieldValueWithoutSearch: (text: string) => void;
  setPlacesList: (placeList: undefined) => void;
  value?: Address;
}) => {
  const [open, setOpen] = useState(false);
  // A pick wrote the text, so blur and Enter must not search it again; typing clears this
  const pickedRef = useRef(false);
  const query = fieldValue === getFormattedAddressForUi(value) ? "" : fieldValue;
  const groups = useMemo(() => presets?.getGroups(query) ?? [], [presets, query]);

  const mergedOptions = useMemo(
    () =>
      presets
        ? [
            ...groups.map((group) => ({
              key: group.key,
              label: group.label,
              options: group.presets.map((preset) => ({
                disabled: preset.disabled,
                label: preset.label,
                value: getPresetValue(preset),
              })),
            })),
            ...options,
          ]
        : options,
    [groups, options, presets]
  );

  const handleTextChange = useCallback(
    (text: string) => {
      // rc-select reports a picked option's value as typed text before calling onSelect
      if (presets && text.startsWith(PRESET_VALUE_PREFIX)) {
        return;
      }
      pickedRef.current = false;
      setFieldValue(text);
    },
    [presets, setFieldValue]
  );

  const handleSelect = useCallback(
    (optionValue: string) => {
      pickedRef.current = true;
      if (!optionValue.startsWith(PRESET_VALUE_PREFIX)) {
        onSelect(optionValue);
        return;
      }
      const preset = groups
        .flatMap((group) => group.presets)
        .find((preset) => getPresetValue(preset) === optionValue);
      if (!preset) {
        return;
      }
      setFieldValueWithoutSearch(preset.address ? getFormattedAddressForUi(preset.address) : "");
      setPlacesList(undefined);
      presets?.onSelect?.(preset, query);
      if (preset.address) {
        onChange?.(preset.address, { geoPoint: preset.geoPoint });
        onEditEnd?.();
      } else {
        // rc-select closes the list after any pick, but an action row keeps it open
        setTimeout(() => setOpen(true));
      }
    },
    [
      groups,
      onChange,
      onEditEnd,
      onSelect,
      presets,
      query,
      setFieldValueWithoutSearch,
      setPlacesList,
    ]
  );

  // The inline picker keeps this hook mounted between edits, so each edit starts unpicked
  const handleFocus = useCallback(() => {
    pickedRef.current = false;
  }, []);

  const handleBlur = useCallback(() => {
    onBlur?.();
    if (presets && pickedRef.current) {
      setFieldValueWithoutSearch(getFormattedAddressForUi(value));
      onEditEnd?.();
    } else {
      onTextBlur();
    }
  }, [onBlur, onEditEnd, onTextBlur, presets, setFieldValueWithoutSearch, value]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      // rc-select picks the highlighted option before this handler runs
      if (event.key === "Enter" && presets && pickedRef.current) {
        return;
      }
      onKeyDown(event);
    },
    [onKeyDown, presets]
  );

  return {
    onBlur: handleBlur,
    onChange: handleTextChange,
    onDropdownVisibleChange: presets ? setOpen : undefined,
    onFocus: handleFocus,
    onKeyDown: handleKeyDown,
    onSelect: handleSelect,
    open: presets ? open : undefined,
    options: mergedOptions,
    showAction: presets ? SHOW_ON_FOCUS : undefined,
  };
};

export default useAddressPresets;
